import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import {
  IconCircle,
  IconFlipHorizontal,
  IconFlipVertical,
  IconFrame,
  IconRotateClockwise2,
  IconSquare,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import type { ComponentType, RefObject } from "react";
import { useState } from "react";
import type { CropperRef } from "react-advanced-cropper";
import type { CropShape } from "../types";
import { CropperDialogActionBtn } from "./cropper-dialog-action-btn";

const SHAPE_OPTIONS: Array<{
  value: CropShape;
  Icon: ComponentType<{ className?: string }>;
  title: string;
}> = [
  { value: "free", Icon: IconFrame, title: "Free form" },
  { value: "square", Icon: IconSquare, title: "Square" },
  { value: "circle", Icon: IconCircle, title: "Circle" },
];

const itemClass =
  "tw-relative tw-flex tw-h-8 tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded tw-px-3 tw-py-0 tw-font-sans tw-text-[14px] tw-font-normal tw-text-[#12033E] tw-outline-none tw-transition-colors tw-duration-200 hover:tw-bg-[#F3F2F5] [&_svg]:!tw-text-[#12033E]";

interface IProps {
  cropperRef: RefObject<CropperRef>;
  cropShape: CropShape;
  onCropShapeChange: (shape: CropShape) => void;
}

export const CropperDialogActions = ({
  cropperRef,
  cropShape,
  onCropShapeChange,
}: IProps) => {
  const [shapeOpen, setShapeOpen] = useState(false);
  const currentShape = SHAPE_OPTIONS.find((o) => o.value === cropShape);
  const ShapeIcon = currentShape?.Icon ?? IconFrame;

  return (
    <>
      <Popover open={shapeOpen} onOpenChange={setShapeOpen} placement="top">
        <Popover.Trigger
          className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-p-2 tw-outline-none tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-bg-blue-100"
          aria-label="Shape"
          aria-expanded={shapeOpen}
        >
          <ShapeIcon
            className={cn(
              "tw-size-4 tw-flex-shrink-0 tw-text-blue",
              cropShape === "circle" || cropShape === "square"
                ? "!tw-fill-none"
                : "tw-fill-blue",
            )}
          />
          <span className="tw-hidden sm:tw-inline tw-whitespace-nowrap tw-font-sans tw-text-xs tw-font-medium tw-text-blue-base">
            Shape
          </span>
        </Popover.Trigger>
        <Popover.Content
          className="tw-z-[10002] tw-min-w-[160px] tw-rounded-lg tw-border tw-border-solid tw-border-[rgba(18,3,62,0.08)] tw-bg-white tw-p-1 tw-shadow-lg"
        >
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            {SHAPE_OPTIONS.map((opt) => {
              const OptIcon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onCropShapeChange(opt.value);
                    setShapeOpen(false);
                  }}
                  className={cn(
                    itemClass,
                    "tw-w-full tw-text-left",
                    cropShape === opt.value && "tw-bg-[#F3F2F5]",
                  )}
                  title={opt.title}
                >
                  <OptIcon className="tw-size-4 tw-flex-shrink-0 !tw-fill-none [&_svg]:!tw-text-[#12033E]" />
                  <span>{opt.title}</span>
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover>
      <CropperDialogActionBtn
        onClick={() => {
          if (!cropperRef.current) return;
          cropperRef.current.zoomImage(0.9);
        }}
        tooltip="Zoom out"
        label="Zoom out"
        icon={<IconZoomOut className="tw-size-4" />}
      />
      <CropperDialogActionBtn
        onClick={() => {
          if (!cropperRef.current) return;
          cropperRef.current.zoomImage(1.1);
        }}
        tooltip="Zoom in"
        label="Zoom in"
        icon={<IconZoomIn className="tw-size-4" />}
      />
      <CropperDialogActionBtn
        onClick={() => {
          if (!cropperRef.current) return;
          cropperRef.current.rotateImage(45);
        }}
        tooltip="Rotate"
        label="Rotate"
        icon={<IconRotateClockwise2 className="tw-size-4" />}
      />
      <CropperDialogActionBtn
        onClick={() => {
          if (!cropperRef.current) return;
          const coordinates = cropperRef.current.getCoordinates();
          cropperRef.current.flipImage(true, false);
          if (coordinates) {
            requestAnimationFrame(() => {
              cropperRef.current?.setCoordinates(coordinates);
            });
          }
        }}
        tooltip="Flip horizontally"
        label="Flip horizontally"
        // Tabler's IconFlipVertical flips along the vertical axis = horizontal flip
        icon={<IconFlipVertical className="tw-size-4" />}
      />
      <CropperDialogActionBtn
        onClick={() => {
          if (!cropperRef.current) return;
          const coordinates = cropperRef.current.getCoordinates();
          cropperRef.current.flipImage(false, true);
          if (coordinates) {
            requestAnimationFrame(() => {
              cropperRef.current?.setCoordinates(coordinates);
            });
          }
        }}
        tooltip="Flip vertically"
        label="Flip vertically"
        // Tabler's IconFlipHorizontal flips along the horizontal axis = vertical flip
        icon={<IconFlipHorizontal className="tw-size-4" />}
      />
    </>
  );
};
